import {
  Accordion,
  Table,
  Loader,
  Title,
  useMantineTheme,
  ActionIcon,
  Transition,
  Flex,
  Text,
  CloseButton,
} from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import { useModals } from "@mantine/modals";
import { IconPencil } from "@tabler/icons";
import { memo, useState, useEffect } from "react";
import { BiBlock } from "react-icons/bi";
import { MdPlaylistAdd } from "react-icons/md";
import { RxDotsVertical } from "react-icons/rx";
import {
  PaymentMethodType,
  useDeleteScheduledTransactionMutation,
  useScheduledTransactionsByClientIdLazyQuery,
} from "src/generated/graphql";
import { CRUDSTATES, EMPLOYEE_ROLE_OBJ, formatter } from "src/utils/constants";
import SchedulePayment from "../ScheduledPayments";
import useWritePermission from "src/hooks/usePermission";

export const columnsTransaction = [
  "Date",
  "Amount",
  "Location",
  "Method",
  "Action",
];

interface IProps {
  clientId?: string;
}

type ISchecduledPayment = {
  state: CRUDSTATES;
  scheduledTransactionId?: string;
  transactionDate?: Date;
};

const ScheduledTransactionHistory = ({ clientId }: IProps) => {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.clients);
  const theme = useMantineTheme();
  const modals = useModals();

  const [activeOption, setActiveOption] = useState<
    { name: string; id: string } | undefined
  >(undefined);
  const outsideClickRef = useClickOutside(() => {
    setActiveOption(undefined);
  });

  const [
    getScheduledTransactions,
    { data, loading: scheduledTransactionsLoading },
  ] = useScheduledTransactionsByClientIdLazyQuery({
    onCompleted: (data) => {},
    fetchPolicy: "network-only",
  });

  const [deleteScheduleTransaction, {}] = useDeleteScheduledTransactionMutation(
    {
      onCompleted: () => {
        onRefetch();
      },
    }
  );

  useEffect(() => {
    clientId &&
      getScheduledTransactions({
        variables: { clientId: clientId! },
      });
  }, [clientId]);

  const handleOpenListMenu = (option: { name: string; id: string }) => {
    setActiveOption(option);
  };

  const handleDeleteScheduleTransaction = (transactionId: string) => {
    transactionId &&
      deleteScheduleTransaction({
        variables: { id: transactionId },
      });
  };

  const onRefetch = () => {
    getScheduledTransactions({
      variables: { clientId: clientId! },
    });
  };

  const handleSchedulePaymentModal = ({
    state,
    scheduledTransactionId,
    transactionDate,
  }: ISchecduledPayment) => {
    modals.openModal({
      closeOnClickOutside: true,
      withCloseButton: false,
      children: (
        <SchedulePayment
          state={state}
          clientId={clientId!}
          scheduledTransactionId={scheduledTransactionId}
          transactionDate={transactionDate}
          onRefetch={onRefetch}
          closeModal={() => modals.closeAll()}
        />
      ),
    });
  };

  return (
    <Table
      sx={{
        thead: {
          tr: {
            th: {
              color: theme.colors.secondaryTextColor[6],
            },
          },
        },
        tbody: {
          tr: {
            "&:nth-of-type(odd)": {
              color: theme.colors.secondaryTextColor[6],
              backgroundColor: theme.colors.secondary[6],
            },
            "&:nth-of-type(even)": {
              color: theme.colors.secondaryTextColor[4],
              backgroundColor: theme.colors.secondary[4],
            },
            td: {
              borderTop: `1px solid ${theme.colors.secondary[2]}`,
            },
          },
        },
      }}
    >
      <thead>
        <tr>
          {columnsTransaction.map((column) => (
            <th
              key={column}
              style={
                column === "Action"
                  ? { justifyContent: "flex-end", display: "flex" }
                  : {}
              }
            >
              {column === "Action" ? (
                <ActionIcon
                  disabled={!isAllowWrite}
                  onClick={() =>
                    handleSchedulePaymentModal({ state: CRUDSTATES.Create })
                  }
                  c={theme.colors.secondaryTextColor[6]}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.colors.secondary[4],
                    },
                  }}
                >
                  <MdPlaylistAdd size={25} />
                </ActionIcon>
              ) : (
                column
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody style={{ overflow: "hidden", width: "100%" }}>
        {data?.scheduledTransactionsByClientId?.map((sc) => {
          const totalCost =
            sc.charges?.reduce(
              (partialSum, charge) => partialSum + charge.cost,
              0
            ) || 0;
          return (
            <tr key={sc.id}>
              <td>{new Date(sc.processAt).toDateString()}</td>
              <td>{formatter.format(totalCost)}</td>
              <td>{sc.location.name}</td>
              <td>
                {sc.transaction?.paymentMethod &&
                  `${
                    sc.transaction.paymentMethod.type === PaymentMethodType.Card
                      ? sc.transaction.paymentMethod.merchant || "Card"
                      : "Bank"
                  } - ${sc.transaction.paymentMethod.mask?.substring(
                    sc.transaction.paymentMethod.mask.length - 4
                  )}`}
              </td>
              <td>
                {isAllowWrite && (
                  <ActionIcon
                    onClick={() =>
                      handleOpenListMenu({
                        name: sc.id,
                        id: sc.id,
                      })
                    }
                    c={theme.colors.secondaryTextColor[6]}
                    sx={{
                      right: -17,
                      "&:hover": {
                        backgroundColor: theme.colors.secondary[4],
                      },
                    }}
                  >
                    <RxDotsVertical />
                  </ActionIcon>
                )}
              </td>
              <Transition
                mounted={activeOption?.id === sc.id}
                transition="slide-left"
                duration={200}
                timingFunction="ease-in-out"
              >
                {(styles) => (
                  <Flex
                    mt={4}
                    justify="space-between"
                    px={2}
                    bg={theme.colors.secondary[2]}
                    style={{
                      ...styles,
                      position: "absolute",
                      float: "left",
                      width: "46%",
                      borderRadius: "5px",
                      left: 23,
                    }}
                    ref={outsideClickRef}
                  >
                    <ActionIcon
                      variant="light"
                      size="md"
                      color="blue.4"
                      my={2}
                      w="45%"
                      mah={25}
                      loading={false}
                      onClick={() =>
                        handleSchedulePaymentModal({
                          state: CRUDSTATES.Update,
                          scheduledTransactionId: sc.id,
                          transactionDate: sc.processAt,
                        })
                      }
                      sx={{
                        backgroundColor: theme.colors.secondary[2],
                        "&:hover": {
                          backgroundColor: theme.colors.secondary[4],
                        },
                      }}
                    >
                      <>
                        <IconPencil size={21} />
                        <Text mx={10}>Edit Transaction</Text>
                      </>
                    </ActionIcon>
                    <ActionIcon
                      variant="light"
                      size="md"
                      color="red.4"
                      my={2}
                      w="45%"
                      mah={25}
                      loading={false}
                      onClick={() => {
                        handleDeleteScheduleTransaction(sc.id);
                      }}
                      sx={{
                        backgroundColor: theme.colors.secondary[2],
                        "&:hover": {
                          backgroundColor: theme.colors.secondary[4],
                        },
                      }}
                    >
                      <>
                        <BiBlock size={21} />
                        <Text mx={10}>Delete Transaction</Text>
                      </>
                    </ActionIcon>
                    <CloseButton
                      size="md"
                      w={"10%"}
                      c={theme.colors.secondaryTextColor[2]}
                      onClick={() => setActiveOption(undefined)}
                      my={2}
                      sx={{
                        backgroundColor: theme.colors.secondary[2],
                        "&:hover": {
                          backgroundColor: theme.colors.secondary[4],
                        },
                      }}
                    />
                  </Flex>
                )}
              </Transition>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default memo(ScheduledTransactionHistory);
